import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { remify } from '@kerfjs/ui/css-values';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { Select } from '@kerfjs/ui/select';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { type IntakeLine, lineEvidenceMessage, lineEvidenceSubmitting } from '../../../data/state.js';

export function EvidenceBenchmarkDialog({
  line,
  base,
  compact,
  lightDismiss,
}: {
  line: IntakeLine;
  base: string;
  compact: boolean;
  lightDismiss: boolean;
}): SafeHtml {
  return (
    <DialogSurface
      label="Add Benchmark"
      className="evidence-benchmark-dialog line-benchmark-dialog"
      lightDismiss={lightDismiss}
      preferredWidth={remify(760)}
      viewportGutter={remify(12)}
      rootAttributes={{ 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            tone="neutral"
            size="default"
            emphasis="plain"
            action="evidence-benchmark-close"
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action="evidence-add-benchmark"
            disabled={lineEvidenceSubmitting.value}
          >
            {lineEvidenceSubmitting.value ? 'Adding benchmark…' : 'Add benchmark and re-check'}
          </Button>
        </>
      }
    >
      <div data-line-id={line.id}>
        <List gap="xs">
          <Text tone="quiet" size="compact">
            Use a source URL, or enter a remembered past-purchase title and note.
          </Text>
          <Grid columns={compact ? 1 : 3} gap="xs">
            <TextField name="evidence-price" type="number" label="Price" />
            <TextField type="text" name="evidence-currency" label="Currency" value={base} />
            <TextField type="text" name="evidence-unit" label="Unit" value={line.unit ?? 'unit'} />
          </Grid>
          <TextField name="evidence-url" type="url" label="Source URL" hint="Optional" />
          <TextField type="text" name="evidence-title" label="Source or Past-Purchase Title" />
          <Grid columns={compact ? 1 : 3} gap="xs">
            <TextField type="text" name="evidence-geography" label="Geography" hint="Optional" />
            <TextField type="text" name="evidence-basis" label="Basis" hint="Optional" />
            <Select
              name="evidence-match"
              label="Match Quality"
              value="equivalent"
              choices={[
                { value: 'exact', label: 'Exact' },
                { value: 'equivalent', label: 'Equivalent' },
                { value: 'proxy', label: 'Proxy' },
              ]}
            />
          </Grid>
          <TextField multiline name="evidence-note" label="Remembered-Price Context or Note" />
          {lineEvidenceMessage.value !== '' ? <Text role="status">{lineEvidenceMessage.value}</Text> : null}
        </List>
      </div>
    </DialogSurface>
  );
}
